<template>
  <slide-over-right
    :id="`${sorId}-${id}`"
    v-on:showModal="showModal"
    editType="standard"
  >
    <loading :active.sync="isLoading"></loading>

    <template v-slot:header>
      <h2 class="font-semibold text-gray-900 text-22px">
        {{ getTitleText }}
      </h2>
    </template>

    <ValidationObserver v-slot="{ handleSubmit }">
      <form @submit.prevent="handleSubmit(submit)" class="px-6">
        <div class="py-1">
          <TCheckbox
            v-model="update_heartbeat_position_frequency"
            :name="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.title.changeLocationUpdateFrequencyCheckBox'
              )
            "
            :label="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.title.changeLocationUpdateFrequencyCheckBox'
              )
            "
            wrapped
          />
        </div>
        <div class="grid grid-cols-2 gap-4">
          <AppInput
            v-model="form.on_ride_location_update_frequency"
            type="richselect"
            :name="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.title.locationUpdateFrequencyOnRide'
              )
            "
            :label="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.title.locationUpdateFrequencyOnRide'
              )
            "
            :placeholder="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.placeHolder.locationUpdateFrequencyOnRide'
              )
            "
            rules="required"
            value-attribute="value"
            text-attribute="text"
            :infoDescription="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.infoDescription.locationUpdateFrequencyOnRide'
              )
            "
            :options="getFrequencyOptionsOnRide"
            :disabled="!update_heartbeat_position_frequency"
            :variant="{
              readonly: !update_heartbeat_position_frequency,
            }"
            :hide-search-box="true"
          />
          <AppInput
            v-model="form.off_ride_location_update_frequency"
            :disabled="!update_heartbeat_position_frequency"
            type="richselect"
            :name="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.title.locationUpdateFrequencyOffRide'
              )
            "
            :label="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.title.locationUpdateFrequencyOffRide'
              )
            "
            :placeholder="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.placeHolder.locationUpdateFrequencyOffRide'
              )
            "
            rules="required"
            value-attribute="value"
            text-attribute="text"
            :infoDescription="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.infoDescription.locationUpdateFrequencyOffRide'
              )
            "
            :options="getFrequencyOptionsOffRide"
            :variant="{
              readonly: !update_heartbeat_position_frequency,
            }"
            :hide-search-box="true"
          />
        </div>

        <div class="grid grid-cols-2 gap-4">
          <AppInput
            v-model="form.low_speed_limit"
            type="richselect"
            :name="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.title.lowSpeedLimit'
              )
            "
            :label="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.title.lowSpeedLimit'
              )
            "
            :placeholder="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.placeHolder.lowSpeedLimit'
              )
            "
            rules="required"
            value-attribute="value"
            text-attribute="text"
            :infoDescription="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.infoDescription.lowSpeedLimit'
              )
            "
            :options="getSpeedLimitOptions"
            :hide-search-box="true"
            :haveUnitText="true"
            unitText="km/h"
          />
          <AppInput
            v-model="form.medium_speed_limit"
            type="richselect"
            :name="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.title.mediumSpeedLimit'
              )
            "
            :label="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.title.mediumSpeedLimit'
              )
            "
            :placeholder="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.placeHolder.mediumSpeedLimit'
              )
            "
            rules="required"
            value-attribute="value"
            text-attribute="text"
            :infoDescription="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.infoDescription.mediumSpeedLimit'
              )
            "
            :options="getSpeedLimitOptions"
            :hide-search-box="true"
            :haveUnitText="true"
            unitText="km/h"
          />
        </div>

        <div class="grid grid-cols-2 gap-4">
          <AppInput
            v-model="form.high_speed_limit"
            type="richselect"
            :name="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.title.highSpeedLimit'
              )
            "
            :label="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.title.highSpeedLimit'
              )
            "
            :placeholder="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.placeHolder.highSpeedLimit'
              )
            "
            rules="required"
            value-attribute="value"
            text-attribute="text"
            :infoDescription="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.infoDescription.highSpeedLimit'
              )
            "
            :options="getSpeedLimitOptions"
            :hide-search-box="true"
            :haveUnitText="true"
            unitText="km/h"
          />

          <AppInput
            v-model="form.max_speed_limit"
            type="richselect"
            :name="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.title.maxSpeedMode'
              )
            "
            :label="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.title.maxSpeedMode'
              )
            "
            :placeholder="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.placeHolder.maxSpeedMode'
              )
            "
            rules="required"
            value-attribute="value"
            text-attribute="text"
            :infoDescription="
              $t(
                'components.fleetManagement.addEdit.steps.configuration.infoDescription.maxSpeedMode'
              )
            "
            :options="getMaxSpeedLimitOptions"
            :hide-search-box="true"
          />
        </div>

        <div class="flex justify-end items-center mt-6">
          <slide-over-right-trigger :id="sorId">
            <anchor-button variant="secondary" @click="closeModal">{{
              $t('components.stepNavigation.cancel')
            }}</anchor-button>
          </slide-over-right-trigger>
          <t-button type="submit" class="ml-3">{{
            $t('components.stepNavigation.update')
          }}</t-button>
        </div>
      </form>
    </ValidationObserver>
  </slide-over-right>
</template>

<script>
import xMan from '@/utils/xMan'
import { EventBus } from '@/utils/EventBus'
import { FleetIoTSettingConfig } from '@/config/FleetIoTSettingConfig'
import SlideOverRight from '@/components/modals/SlideOverRight'
import SlideOverRightTrigger from '@/components/modals/SlideOverRightTrigger'
import AnchorButton from '@/components/form/AnchorButton.vue'
import { useEndpoints } from '@/composables'

export default {
  name: 'ChangeFleetIoTSettings',
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
    fleetId: {
      type: Object,
      required: true,
    },
  },
  components: {
    SlideOverRight,
    SlideOverRightTrigger,
    AnchorButton,
  },
  data() {
    return {
      isLoading: false,
      update_heartbeat_position_frequency: false,
      form: {
        on_ride_location_update_frequency: 0,
        off_ride_location_update_frequency: 0,
        low_speed_limit: 0,
        medium_speed_limit: 0,
        high_speed_limit: 0,
        max_speed_limit: 'HIGH',
      },
      sorId: FleetIoTSettingConfig.events.sorId,
    }
  },
  computed: {
    getTitleText() {
      return 'Update IoT Settings'
    },
    getFrequencyOptionsOnRide() {
      return [
        { value: 10, text: '10s' },
        { value: 20, text: '20s' },
        { value: 30, text: '30s' },
        { value: 60, text: '1m' },
        { value: 90, text: '1m 30s' },
        { value: 120, text: '2m' },
        { value: 150, text: '2m 30s' },
        { value: 180, text: '3m' },
        { value: 210, text: '3m 30s' },
        { value: 240, text: '4m' },
      ]
    },
    getFrequencyOptionsOffRide() {
      return [
        { value: 30, text: '30s' },
        { value: 60, text: '1m' },
        { value: 90, text: '1m 30s' },
        { value: 120, text: '2m' },
        { value: 150, text: '2m 30s' },
        { value: 180, text: '3m' },
        { value: 210, text: '3m 30s' },
        { value: 240, text: '4m' },
      ]
    },
    getSpeedLimitOptions() {
      return [
        { value: 6, text: 6 },
        { value: 7, text: 7 },
        { value: 8, text: 8 },
        { value: 9, text: 9 },
        { value: 10, text: 10 },
        { value: 11, text: 11 },
        { value: 12, text: 12 },
        { value: 13, text: 13 },
        { value: 14, text: 14 },
        { value: 15, text: 15 },
        { value: 16, text: 16 },
        { value: 17, text: 17 },
        { value: 18, text: 18 },
        { value: 19, text: 19 },
        { value: 20, text: 20 },
        { value: 21, text: 21 },
        { value: 22, text: 22 },
        { value: 23, text: 23 },
        { value: 24, text: 24 },
        { value: 25, text: 25 },
      ]
    },
    getMaxSpeedLimitOptions() {
      return [
        { value: 'LOW', text: 'Low' },
        { value: 'MEDIUM', text: 'Medium' },
        { value: 'HIGH', text: 'High' },
      ]
    },
  },
  mounted() {
    EventBus.$on(FleetIoTSettingConfig.events.editingData, (item) => {
      console.log('test-d', item)
      this.form = { ...item }
    })
  },
  methods: {
    showModal() {
      dispatchEvent(new Event(FleetIoTSettingConfig.events.sorToggle(this.id)))
    },
    closeModal() {
      dispatchEvent(new Event(FleetIoTSettingConfig.events.sorClose(this.id)))
    },
    submit: async function() {
      this.isLoading = true
      let method = 'PATCH'
      let url = useEndpoints.fleet.update(this.fleetId)
      const formDataProxy = { ...this.form }
      if (!this.update_heartbeat_position_frequency) {
        delete formDataProxy.on_ride_location_update_frequency
        delete formDataProxy.off_ride_location_update_frequency
      }
      let data = new xMan(formDataProxy).toFormData()

      let message = 'IoT Settings updated successfully'

      try {
        let response = await this.$http({ method, url, data })
        this.isLoading = false
        // Close the slideover
        dispatchEvent(new Event(FleetIoTSettingConfig.events.sorClose(this.id)))
        // Refetch the indexData
        dispatchEvent(new Event(FleetIoTSettingConfig.events.refresh))

        this.$emit('refresh')
        // Reset the form
        this.form = {}
        this.$notify(
          {
            group: 'generic',
            type: 'success',
            title: 'Success',
            text: message,
          },
          3000
        )
        // Resolved
        return response.status
      } catch (error) {
        // todo: remove logs
        console.log('fleetIoTSetting-err', { error })
        // todo: show server errors
        this.isLoading = false

        this.$notify(
          {
            group: 'bottomRight',
            type: 'error',
            title: 'Failed',
            text: `${error.response.data.message}`,
          },
          5000
        )
      }
    },
  },
}
</script>

<style></style>
